import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api/client';
import { ApiError, errorMessageKey, unwrap } from '../../api/api-error';
import { useMe } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { AuthLayout } from '../../components/auth/auth-dialog';
import { Notice } from '../../components/ui/notice';
import { useI18n } from '../../i18n/i18n';
import { takeFragmentToken } from '../../lib/fragment-token';
import {
  clearPendingInvitation,
  readPendingInvitation,
  savePendingInvitation,
} from '../../lib/pending-invitation';
import { INBOX_KEY, WORKSPACES_KEY } from '../../workspaces/workspaces-api';
import { paths } from '../../app/paths';

/** Токен из ссылки письма или сохранённый до входа: ссылка ведёт и тех, у кого ещё нет аккаунта. */
/** Ошибки, после которых по этой ссылке уже ничего не выйдет: токен больше не храним. */
const FINAL_CODES = new Set([
  'INVITATION_INVALID',
  'INVITATION_NOT_FOUND',
  'INVITATION_EMAIL_MISMATCH',
]);

function forgetIfFinal(error: unknown): void {
  if (error instanceof ApiError && FINAL_CODES.has(error.code)) clearPendingInvitation();
}

function initialToken(): string | null {
  const fromLink = takeFragmentToken();
  if (fromLink !== null) {
    savePendingInvitation(fromLink);
    return fromLink;
  }
  return readPendingInvitation();
}

/**
 * Приглашение по ссылке `/invitations/accept#token=…` (ADR-0014). Без аккаунта — войти или
 * зарегистрироваться на адрес приглашения; после входа главная вернёт сюда.
 */
export function AcceptInvitationPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const me = useMe();
  const [token] = useState(initialToken);
  const preview = useQuery({
    queryKey: ['invitations', 'preview', token],
    enabled: token !== null,
    retry: false,
    queryFn: async () => {
      try {
        return unwrap(
          await api.POST('/api/v1/invitations/preview', { body: { token: token ?? '' } }),
        );
      } catch (error) {
        // Иначе главная после каждого входа возвращала бы на мёртвую ссылку.
        forgetIfFinal(error);
        throw error;
      }
    },
  });
  const accept = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST('/api/v1/invitations/accept', { body: { token: token ?? '' } })),
    onSuccess: async (accepted) => {
      clearPendingInvitation();
      await queryClient.invalidateQueries({ queryKey: WORKSPACES_KEY });
      await queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      await navigate(paths.workspace(accepted.workspaceId), { replace: true });
    },
    onError: forgetIfFinal,
  });

  if (token === null || preview.isError) {
    return (
      <AuthLayout title={t('invitations.accept.title')}>
        <Notice tone="danger">
          {t(preview.isError ? errorMessageKey(preview.error) : 'error.INVITATION_INVALID')}
        </Notice>
        <Button
          type="button"
          variant="link"
          onClick={() => {
            clearPendingInvitation();
            void navigate('/');
          }}
        >
          {t('notFound.back')}
        </Button>
      </AuthLayout>
    );
  }
  if (preview.isPending || me.isPending) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }
  const invitation = preview.data;
  const profile = me.data ?? null;

  return (
    <AuthLayout title={t('invitations.accept.title')}>
      <p className="text-sm">
        <span className="font-medium">{invitation.inviterName ?? t('common.formerUser')}</span>{' '}
        {t('invitations.accept.invites')}{' '}
        <span className="font-medium">{invitation.workspace.name}</span>.
      </p>
      <p className="text-sm text-muted-foreground">
        {t('invitations.accept.address')} {invitation.email}
      </p>
      {profile !== null ? (
        <>
          {profile.email !== invitation.email && (
            <Notice>{t('invitations.accept.otherAccount')}</Notice>
          )}
          {accept.isError && <Notice tone="danger">{t(errorMessageKey(accept.error))}</Notice>}
          <Button
            type="button"
            className="w-full"
            disabled={accept.isPending}
            onClick={() => accept.mutate()}
          >
            {t('invitations.accept')}
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('invitations.accept.signInFirst')}</p>
          <div className="flex gap-3">
            <Link to="/register" className="text-sm text-primary hover:underline">
              {t('auth.login.toRegister')}
            </Link>
            <Link
              to="/login"
              state={{ from: '/invitations/accept' }}
              className="text-sm text-primary hover:underline"
            >
              {t('nav.login')}
            </Link>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
